/**
 * Task-node data layer — TanStack Query hooks over the Phase-1 task API
 * (`/api/teams/{teamId}/tasks/...`). This is the only place the editor
 * talks to the server; the rendering components consume these hooks and
 * never call `apiFetch` directly (the clean query/view split §A7 wants).
 *
 * Issue #759 (Phase 2). The query is keyed `["task-nodes", teamId]`; every
 * mutation invalidates that key on success so the canvas + rail re-derive
 * from a single fresh source of truth. Mutations are deliberately thin —
 * one per API verb — so a view never has to know the route shapes.
 *
 * Isolation: imports only the generic `apiFetch`/`ApiError` and the
 * generated schema types — no scheduler coupling.
 *
 * Issue #893 (optimistic updates). Every mutation now applies its edit to
 * the cached flat node list *inside `onMutate`* (before the request lands)
 * so the canvas + rail re-render instantly; `onError` rolls the snapshot
 * back, and `onSettled` invalidates to reconcile temp ids + tree-derived
 * roll-ups against the server's truth. The query keeps its 30 s
 * `staleTime`, so the reconciling refetch is the *only* extra fetch — the
 * perceived "create → see it" latency drops from a round-trip + full
 * refetch to a synchronous cache write.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";

import { apiFetch } from "../../api/client";
import type { components } from "../../api/schema.gen";
import type { TaskState } from "../tree/state";

/**
 * Local extension of the generated `TaskNodeWithAssignments` shape with the
 * round-4 ⑥ (#1043) fields the backend now returns on every read:
 *
 *   - `layout_x` / `layout_y` — pinned canvas position (px, plane-coord
 *     pre-pan/pre-zoom). Both NULL ⇒ the node flows via the tidy-tree.
 *   - `kind` — the node kind (`task` is the pre-existing semantic; the new
 *     `link_block` is an off-tree freeform annotation).
 *
 * Typed here (NOT regenerated into `schema.gen.ts`) per the cookbook: a
 * naive `make gen-types` pulls hundreds of lines of OTHER windows' drift
 * (see `feedback_archaeology_squash_merge_masking` + the orchestrator's
 * `schema.gen.ts regen trap` note). Backend round-trips the same fields;
 * the SPA reads them via this intersected type so every downstream consumer
 * sees them.
 */
type GeneratedTaskNodeWithAssignments = components["schemas"]["TaskNodeWithAssignments"];

export type TaskNodeKind = "task" | "link_block";

export type TaskNodeWithAssignments = GeneratedTaskNodeWithAssignments & {
  layout_x: number | null;
  layout_y: number | null;
  kind: TaskNodeKind;
};

type GeneratedNodeResponse = components["schemas"]["NodeResponse"];
/**
 * `NodeResponse` with the extended node shape so the position + link_block
 * mutations return the canonical extended type (matches the wire body).
 */
type NodeResponse = Omit<GeneratedNodeResponse, "node"> & { node: TaskNodeWithAssignments };
type AssignmentResponse = components["schemas"]["AssignmentResponse"];
type TaskAssignmentRead = components["schemas"]["TaskAssignmentRead"];

/** Cache key for a team's task-node list. Exported for tests + invalidation. */
export const TASK_NODES_KEY = (teamId: string) => ["task-nodes", teamId] as const;

const STALE_TIME_MS = 30_000;

/** The "not started" state — kept in sync with the `state` vocabulary (`tree/state.ts`). */
const DEFAULT_STATE: TaskState = "planned";

function tasksBase(teamId: string): string {
  return `/api/teams/${teamId}/tasks`;
}

// ─── Optimistic-cache plumbing (#893) ─────────────────────────────────────
//
// One shared shape for the `onMutate` snapshot/rollback, plus a tiny set of
// pure helpers that transform the cached flat list. Keeping the cache writes
// pure + co-located makes each mutation's optimistic intent obvious and easy
// to test in isolation.

/** What `onMutate` stashes so `onError` can restore the pre-mutation cache. */
interface OptimisticCtx {
  previous: TaskNodeWithAssignments[] | undefined;
}

/**
 * Snapshot the current node list, apply a pure `edit`, and write the result
 * back to the cache. Returns the snapshot for rollback. Callers must have
 * already `cancelQueries`'d so no in-flight refetch clobbers the edit.
 */
async function applyOptimistic(
  qc: QueryClient,
  teamId: string,
  edit: (list: TaskNodeWithAssignments[]) => TaskNodeWithAssignments[],
): Promise<OptimisticCtx> {
  await qc.cancelQueries({ queryKey: TASK_NODES_KEY(teamId) });
  const previous = qc.getQueryData<TaskNodeWithAssignments[]>(TASK_NODES_KEY(teamId));
  qc.setQueryData<TaskNodeWithAssignments[]>(TASK_NODES_KEY(teamId), (current) =>
    edit(current ?? []),
  );
  return { previous };
}

/** Restore the pre-mutation snapshot (no-op if `onMutate` never ran). */
function rollback(qc: QueryClient, teamId: string, ctx: OptimisticCtx | undefined): void {
  if (ctx?.previous !== undefined) {
    qc.setQueryData<TaskNodeWithAssignments[]>(TASK_NODES_KEY(teamId), ctx.previous);
  }
}

/** Reconcile the optimistic cache against the server (temp ids → real ids, roll-ups). */
function reconcile(qc: QueryClient, teamId: string): void {
  void qc.invalidateQueries({ queryKey: TASK_NODES_KEY(teamId) });
}

/**
 * The whole-team node list (flat; the SPA nests it via `buildTree`). Per
 * §A8 the whole tree is visible to every member, so this fetch is the same
 * for leads and members; per-role *write* gating is enforced server-side.
 */
export function useTaskNodes(teamId: string | undefined) {
  return useQuery({
    queryKey: TASK_NODES_KEY(teamId ?? ""),
    enabled: Boolean(teamId),
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () => apiFetch<TaskNodeWithAssignments[]>(`${tasksBase(teamId as string)}/nodes`),
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────
//
// All five share the same optimistic contract (#893): `onMutate` applies the
// edit to the cached flat list and snapshots the prior state, `onError` rolls
// back, `onSettled` invalidates to reconcile. The canvas/rail re-derive from
// the cache synchronously so the edit feels instant; the eventual refetch
// fixes temp ids + tree-derived roll-ups.

interface CreateNodeArgs {
  title: string;
  parentId?: string | null;
  orderIndex?: number;
}

export function useCreateTaskNode(teamId: string | undefined) {
  const qc = useQueryClient();
  const team = teamId ?? "";
  return useMutation({
    mutationFn: ({ title, parentId, orderIndex }: CreateNodeArgs) =>
      apiFetch<NodeResponse>(`${tasksBase(teamId as string)}/nodes`, {
        method: "POST",
        body: JSON.stringify({
          title,
          parent_id: parentId ?? null,
          order_index: orderIndex ?? 0,
        }),
      }),
    onMutate: ({ title, parentId, orderIndex }: CreateNodeArgs) => {
      // A complete `TaskNodeWithAssignments` so `buildTree` never crashes on
      // the optimistic node: real `parent_id`/`order_index`/`title`, the
      // "not started" state, empty assignments, and zeroed/null roll-up
      // fields. The temp id is replaced by the server id on `onSettled`.
      const now = new Date().toISOString();
      const optimistic: TaskNodeWithAssignments = {
        id: `optimistic-${crypto.randomUUID()}`,
        team_id: team,
        parent_id: parentId ?? null,
        title,
        description: null,
        state: DEFAULT_STATE,
        order_index: orderIndex ?? 0,
        estimate_minutes: null,
        target_date: null,
        assignments: [],
        archived_at: null,
        created_at: now,
        created_by: team, // placeholder; reconciled on refetch
        // Round-4 ⑥ (#1043) — a newly-created node starts unpinned (the
        // tidy-tree places it) and is a regular `task` kind. The dedicated
        // link_block creation path uses `useCreateLinkBlock`, not this hook.
        layout_x: null,
        layout_y: null,
        // #1228 added a `last_note_at` roll-up; a brand-new node has no notes.
        last_note_at: null,
        kind: "task",
      };
      return applyOptimistic(qc, team, (list) => [...list, optimistic]);
    },
    onError: (_e, _vars, ctx) => rollback(qc, team, ctx),
    onSettled: () => reconcile(qc, team),
  });
}

interface PatchNodeArgs {
  nodeId: string;
  patch: {
    title?: string;
    description?: string | null;
    parentId?: string | null;
    orderIndex?: number;
    state?: TaskState;
    /**
     * The optional target date (B2.11, #1043) as an ISO `YYYY-MM-DD` string, or
     * `null` to clear it. Omit the key to leave it untouched. Typed here on the
     * hook (not via `schema.gen.ts`) so the SPA need not regen the route shape.
     */
    targetDate?: string | null;
    /**
     * The optional coarse estimate (B2.12, #1043) in minutes, or `null` to clear
     * it. Omit the key to leave it untouched. Typed here on the hook (not via
     * `schema.gen.ts`) for the same reason as `targetDate`.
     */
    estimateMinutes?: number | null;
    /** #2631 - start time (ISO datetime), minutes and place; `null` clears. */
    startsAt?: string | null;
    durationMinutes?: number | null;
    place?: string | null;
  };
}

export function usePatchTaskNode(teamId: string | undefined) {
  const qc = useQueryClient();
  const team = teamId ?? "";
  return useMutation({
    mutationFn: ({ nodeId, patch }: PatchNodeArgs) => {
      // Translate the SPA's camelCase patch to the wire's snake_case body,
      // including only the fields the caller actually set (each present
      // field becomes exactly one server-side event).
      const body: Record<string, unknown> = {};
      if (patch.title !== undefined) body.title = patch.title;
      if (patch.description !== undefined) body.description = patch.description;
      if (patch.parentId !== undefined) body.parent_id = patch.parentId;
      if (patch.orderIndex !== undefined) body.order_index = patch.orderIndex;
      if (patch.state !== undefined) body.state = patch.state;
      if (patch.targetDate !== undefined) body.target_date = patch.targetDate;
      if (patch.estimateMinutes !== undefined) body.estimate_minutes = patch.estimateMinutes;
      if (patch.startsAt !== undefined) body.starts_at = patch.startsAt;
      if (patch.durationMinutes !== undefined) body.duration_minutes = patch.durationMinutes;
      if (patch.place !== undefined) body.place = patch.place;
      return apiFetch<NodeResponse>(`${tasksBase(teamId as string)}/nodes/${nodeId}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
    },
    onMutate: ({ nodeId, patch }: PatchNodeArgs) =>
      // Shallow-merge only the camelCase fields the caller set onto the
      // matching node, mapping each back to its wire column. Untouched
      // fields (assignments, roll-up timestamps) are preserved.
      applyOptimistic(qc, team, (list) =>
        list.map((node) => {
          if (node.id !== nodeId) return node;
          const next: TaskNodeWithAssignments = { ...node };
          if (patch.title !== undefined) next.title = patch.title;
          if (patch.description !== undefined) next.description = patch.description;
          if (patch.parentId !== undefined) next.parent_id = patch.parentId;
          if (patch.orderIndex !== undefined) next.order_index = patch.orderIndex;
          if (patch.state !== undefined) next.state = patch.state;
          if (patch.targetDate !== undefined) next.target_date = patch.targetDate;
          if (patch.estimateMinutes !== undefined) next.estimate_minutes = patch.estimateMinutes;
          if (patch.startsAt !== undefined) next.starts_at = patch.startsAt;
          if (patch.durationMinutes !== undefined) next.duration_minutes = patch.durationMinutes;
          if (patch.place !== undefined) next.place = patch.place;
          return next;
        }),
      ),
    onError: (_e, _vars, ctx) => rollback(qc, team, ctx),
    onSettled: () => reconcile(qc, team),
  });
}

/**
 * Collect a node id + every (live) descendant id from the flat node list, so an
 * optimistic cascade-archive can drop the whole branch in one cache write rather
 * than re-rooting the still-present children (which is exactly what a single-node
 * archive does — see #1087 / #1043). Walks the `parent_id` adjacency top-down;
 * `seen` guards a corrupt cycle. For `cascade=false` this returns just `{rootId}`.
 */
function collectSubtreeIds(
  list: TaskNodeWithAssignments[],
  rootId: string,
  cascade: boolean,
): Set<string> {
  const ids = new Set<string>([rootId]);
  if (!cascade) return ids;
  const childrenByParent = new Map<string, string[]>();
  for (const node of list) {
    if (node.parent_id) {
      const kids = childrenByParent.get(node.parent_id) ?? [];
      kids.push(node.id);
      childrenByParent.set(node.parent_id, kids);
    }
  }
  const queue = [rootId];
  while (queue.length > 0) {
    const current = queue.shift() as string;
    for (const childId of childrenByParent.get(current) ?? []) {
      if (!ids.has(childId)) {
        ids.add(childId);
        queue.push(childId);
      }
    }
  }
  return ids;
}

interface ArchiveNodeArgs {
  nodeId: string;
  /**
   * When true, archive the node AND its whole live subtree in one recoverable
   * action (the backend `cascade` flag, #1043). Defaults to false — the single-
   * node, leaf-safe behaviour, byte-unchanged on the wire.
   */
  cascade?: boolean;
}

export function useArchiveTaskNode(teamId: string | undefined) {
  const qc = useQueryClient();
  const team = teamId ?? "";
  return useMutation({
    mutationFn: ({ nodeId, cascade }: ArchiveNodeArgs) =>
      apiFetch<NodeResponse>(`${tasksBase(teamId as string)}/nodes/${nodeId}/archive`, {
        method: "POST",
        // Only send the body when cascading; an absent body keeps the single-
        // node request byte-unchanged (the route defaults `cascade` to false).
        ...(cascade ? { body: JSON.stringify({ cascade: true }) } : {}),
      }),
    onMutate: ({ nodeId, cascade }: ArchiveNodeArgs) =>
      // GET hides archived nodes, so drop the archived branch from the cache.
      // For a single node that's just the node; for a cascade it's the node +
      // every live descendant (so children are NOT re-rooted optimistically).
      // `onSettled` reconciles against the server's truth either way.
      applyOptimistic(qc, team, (list) => {
        const toDrop = collectSubtreeIds(list, nodeId, cascade ?? false);
        return list.filter((node) => !toDrop.has(node.id));
      }),
    onError: (_e, _vars, ctx) => rollback(qc, team, ctx),
    onSettled: () => reconcile(qc, team),
  });
}

interface RestoreNodeArgs {
  nodeId: string;
}

/**
 * Restore a previously-archived node by calling `POST .../restore` (#1043
 * round-4 ⑤.b). The endpoint returns the now-live node (same `NodeResponse`
 * shape as archive) and emits exactly one `node_restored` event on the wire
 * — none on the idempotent no-op path (already-live nodes are returned
 * untouched).
 *
 * No optimistic cache write: the panel that calls this hook reads from the
 * `include_archived=true` query (a separate listing), not the live tree
 * cache, so the canvas doesn't need an instant local edit. `onSettled`
 * invalidates the live-tree key so a re-render picks the node back up the
 * moment the mutation lands.
 */
export function useRestoreTaskNode(teamId: string | undefined, opts?: { onSuccess?: () => void }) {
  const qc = useQueryClient();
  const team = teamId ?? "";
  return useMutation({
    mutationFn: ({ nodeId }: RestoreNodeArgs) =>
      apiFetch<NodeResponse>(`${tasksBase(teamId as string)}/nodes/${nodeId}/restore`, {
        method: "POST",
      }),
    onSuccess: () => {
      opts?.onSuccess?.();
    },
    onSettled: () => reconcile(qc, team),
  });
}

interface AssignArgs {
  nodeId: string;
  membershipId: string;
}

export function useAssignTaskMember(teamId: string | undefined) {
  const qc = useQueryClient();
  const team = teamId ?? "";
  return useMutation({
    mutationFn: ({ nodeId, membershipId }: AssignArgs) =>
      apiFetch<AssignmentResponse>(`${tasksBase(teamId as string)}/nodes/${nodeId}/assignments`, {
        method: "POST",
        body: JSON.stringify({ membership_id: membershipId }),
      }),
    onMutate: ({ nodeId, membershipId }: AssignArgs) =>
      // Add the member to the node's assignments, idempotently (a duplicate
      // optimistic add would otherwise double-count). The temp assignment id
      // is replaced by the real one on `onSettled`.
      applyOptimistic(qc, team, (list) =>
        list.map((node) => {
          if (node.id !== nodeId) return node;
          if (node.assignments.some((a) => a.membership_id === membershipId)) return node;
          const optimisticAssignment: TaskAssignmentRead = {
            id: `optimistic-${crypto.randomUUID()}`,
            team_id: team,
            node_id: nodeId,
            membership_id: membershipId,
            created_at: new Date().toISOString(),
          };
          return { ...node, assignments: [...node.assignments, optimisticAssignment] };
        }),
      ),
    onError: (_e, _vars, ctx) => rollback(qc, team, ctx),
    onSettled: () => reconcile(qc, team),
  });
}

interface SetPositionArgs {
  nodeId: string;
  /** Pinned X (px, plane-coord), or null to reset to tidy-tree placement. */
  x: number | null;
  /** Pinned Y (px, plane-coord), or null to reset. */
  y: number | null;
}

/**
 * Pin (or reset) a node's canvas position via `PATCH .../position` (round-4 ⑥,
 * #1043). The backend is idempotent on no-change; the FE optimistic write
 * mirrors the same semantic — set both fields on the cached node so the canvas
 * re-renders instantly, and let `onSettled` reconcile against the server (which
 * is the source of truth for whether an event was actually emitted).
 *
 * About the WORK's layout, never a fact about a person (§A9).
 */
export function useSetNodePosition(teamId: string | undefined) {
  const qc = useQueryClient();
  const team = teamId ?? "";
  return useMutation({
    mutationFn: ({ nodeId, x, y }: SetPositionArgs) =>
      apiFetch<NodeResponse>(`${tasksBase(teamId as string)}/nodes/${nodeId}/position`, {
        method: "PATCH",
        body: JSON.stringify({ x, y }),
      }),
    onMutate: ({ nodeId, x, y }: SetPositionArgs) =>
      applyOptimistic(qc, team, (list) =>
        list.map((node) => (node.id === nodeId ? { ...node, layout_x: x, layout_y: y } : node)),
      ),
    onError: (_e, _vars, ctx) => rollback(qc, team, ctx),
    onSettled: () => reconcile(qc, team),
  });
}

interface CreateLinkBlockArgs {
  title: string;
  x: number;
  y: number;
}

/**
 * Create a freeform `link_block` node via `POST .../link_blocks` (round-4 ⑥).
 *
 * A link_block is an OFF-TREE freeform annotation: no `parent_id`, not in any
 * outcome rollup, not in the outline rail. The canvas's "+ link block"
 * affordance fires this hook with the lead's title + the viewport-centre
 * `(x, y)` so the new block lands pinned at a sensible place.
 *
 * Optimistic: the new block is added to the cached flat list with its `kind`
 * + pinned coordinates set, so the canvas paints it immediately. The temp
 * id is swapped for the real id on `onSettled`.
 */
export function useCreateLinkBlock(teamId: string | undefined) {
  const qc = useQueryClient();
  const team = teamId ?? "";
  return useMutation({
    mutationFn: ({ title, x, y }: CreateLinkBlockArgs) =>
      apiFetch<NodeResponse>(`${tasksBase(teamId as string)}/link_blocks`, {
        method: "POST",
        body: JSON.stringify({ title, x, y }),
      }),
    onMutate: ({ title, x, y }: CreateLinkBlockArgs) => {
      const now = new Date().toISOString();
      const optimistic: TaskNodeWithAssignments = {
        id: `optimistic-${crypto.randomUUID()}`,
        team_id: team,
        // link_blocks NEVER have a parent (off-tree, by construction).
        parent_id: null,
        title,
        description: null,
        state: DEFAULT_STATE,
        order_index: 0,
        estimate_minutes: null,
        target_date: null,
        assignments: [],
        archived_at: null,
        created_at: now,
        created_by: team,
        layout_x: x,
        layout_y: y,
        // #1228 added a `last_note_at` roll-up; a brand-new block has no notes.
        last_note_at: null,
        kind: "link_block",
      };
      return applyOptimistic(qc, team, (list) => [...list, optimistic]);
    },
    onError: (_e, _vars, ctx) => rollback(qc, team, ctx),
    onSettled: () => reconcile(qc, team),
  });
}

export function useUnassignTaskMember(teamId: string | undefined) {
  const qc = useQueryClient();
  const team = teamId ?? "";
  return useMutation({
    mutationFn: ({ nodeId, membershipId }: AssignArgs) =>
      apiFetch<void>(`${tasksBase(teamId as string)}/nodes/${nodeId}/assignments/${membershipId}`, {
        method: "DELETE",
      }),
    onMutate: ({ nodeId, membershipId }: AssignArgs) =>
      // Drop the member from the node's assignments by membership id.
      applyOptimistic(qc, team, (list) =>
        list.map((node) =>
          node.id === nodeId
            ? {
                ...node,
                assignments: node.assignments.filter((a) => a.membership_id !== membershipId),
              }
            : node,
        ),
      ),
    onError: (_e, _vars, ctx) => rollback(qc, team, ctx),
    onSettled: () => reconcile(qc, team),
  });
}
